/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';
import objectHash from 'object-hash';

import CommunicatorUtility from '../communicator/utility/communicator-utility';
import TrackPlanCommunicator from './track-plan-communicator';

/**
 * @private
 * @typedef {import('../playlist/playlist-attrs').PlaylistAttrs} PlaylistAttrs
 * @typedef {import('../track/track-attrs').TrackAttrs} TrackAttrs
 */

/**
 * Komunikátor přehrávaných stop playlistu pro Ki-Wi Server.
 */
class TrackCommunicator {
  /**
   * Zpracuje seznam stop a zanořených modelů.
   * @param {Object[]} jsonArray Část odpovědi na obsah zařízení se seznamem stop.
   * @param {PlaylistAttrs['id']} parentPlaylistId Identifikátor playlistu, do kterého stopa patří.
   * @returns {TrackAttrs[]} Pole datových atributů ke vložení do datového skladu.
   */
  static parseMany(jsonArray, parentPlaylistId) {
    if (isNil(parentPlaylistId)) {
      throw new TypeError('Missing parent playlist.');
    }
    return jsonArray.map((jsonObject, index) => {
      const track = this.parse(jsonObject, parentPlaylistId, index);
      return track;
    });
  }

  /**
   * Zpracuje jednu stopu.
   * @param {Object} json Část odpovědi na obsah zařízení s jednou stopou.
   * @param {PlaylistAttrs['id']} parentPlaylistId Identifikátor playlistu, do kterého stopa patří.
   * @param {number} index Pořadí stopy v rodičovském playlistu.
   * @private
   * @returns {TrackAttrs}
   */
  static parse(json, parentPlaylistId, index) {
    // Načte se seznam položek `TrackPlan` ve formátu DHTMLX Scheduler.
    /** @type {any[]} */
    const trackPlans = json.trackPlans ?? [];
    return {
      ...CommunicatorUtility.parseReferenceContentAttrs(json.ref),
      // TODO V datech chybí id.
      // - Bude docházet ke zbytečným změnám při změně pořadí.
      // - Šlo by vylepšit navázáním na playlistId nebo mediumId, ale ty mohou být duplikované.
      // - Tato duplikace by se ještě musela ošetřit např. identifikátor sestavit jako 1. mediumId v daném seznamu.
      id: objectHash({ parentPlaylistId, index }),
      index,
      parentPlaylistId,
      // Převedou se události z DHTMLX do iCalendar formátu.
      planIcal: TrackPlanCommunicator.parseIcal(trackPlans),
      // Pozor, jen zde u tracků není playLength objekt s délkou ve stringu, ale number!
      playDuration: CommunicatorUtility.parseTime(json.schedule.duration),
      volume: CommunicatorUtility.parsePercentage(json.schedule.volume)
    };
  }
}

export default TrackCommunicator;
